/**
 * Source chips — what happens when you click one.
 *
 * Port of `srcClick(s)` (demo line 4034) and the slice of `const SRC = { … }`
 * (demo line 1875) it can reach.
 *
 * ## The contract
 *
 * Every assistant answer ends with a `SOURCES:` line. `splitSources()` in
 * `./local-answer.ts` turns that into up to six chip labels; this module turns
 * one chip label into somewhere to go. There are exactly three outcomes, the
 * demo's three:
 *
 * | Outcome | Demo | Here |
 * |---|---|---|
 * | A controlled document | `openDoc('doc:' + file)` | `{kind: 'route'}` to the data browser, `?doc=<file>` |
 * | A slice of a source system's table | `audit(srcKey, filter)` | `{kind: 'drawer'}` with a {@link SourceTablePayload} |
 * | A page | `show('alerts')`, `show('machine', id)` | `{kind: 'route'}` via `routeForView()` |
 * | Nothing matched | `toast(s)` | `{kind: 'none'}` — the caller toasts the label |
 *
 * ## Fidelity notes
 *
 * - The demo's `map` object declared `rate_card` and `shift_records` **twice**.
 *   JavaScript keeps the first position and the last value; {@link SOURCE_MAP}
 *   is the deduplicated result, in the demo's iteration order, because the
 *   loop is order-sensitive (it returns on the first substring hit).
 * - Matching is the demo's: the lowercased label must contain either the key
 *   with underscores or the key with underscores replaced by spaces.
 * - A machine id anywhere in the label (`/\b([JLPTS]-\d\d)\b/`) becomes the
 *   drawer's row filter, exactly as `audit(v, m ? {machine_id: m[1]} : {})`.
 * - Documents are routed rather than drawered. The demo's `openDoc()` opened a
 *   lake document in its own overlay; this port sends the reader to the data
 *   browser with the file selected, which is where that overlay now lives.
 *
 * Engine layer (layer 2 of the 4-layer architecture): pure apart from the
 * `loadOemDomain()` fetches the table slices need. No React, no DOM.
 *
 * @see src/versions/v1/chrome-oem/SourceDrawerBody.tsx — what renders the payload
 * @see src/versions/v1/shared/oem-routes.ts — `routeForView()`
 */

import type { SourcePayload, SourceTablePayload } from '../../chrome-oem/SourceDrawerBody.tsx';
import {
  COST_PER_MACHINE,
  ENERGY_FACTORS,
  FLEET,
  HEALTH,
  hasMachine,
} from '../../data/core.ts';
import { unpackTable } from '../columnar.ts';
import { routeForView } from '../oem-routes.ts';
import { loadOemDomain } from '../useOemData.ts';
import type { ShiftDailyRow, DailyMetricRow } from '../../schema/telemetry.ts';
import type { WorkOrder } from '../../schema/work-order.ts';

/** Where a source chip leads. */
export type SourceResolution =
  | { kind: 'route'; to: string }
  | { kind: 'drawer'; payload: SourcePayload }
  | { kind: 'none'; label: string };

// ---------------------------------------------------------------------------
// The reachable slice of the demo's SRC registry
// ---------------------------------------------------------------------------

/** One entry of the demo's `SRC` table (demo line 1875). */
interface SourceTableSpec {
  /** Drawer heading. */
  readonly title: string;
  /** Path inside the data lake, shown as provenance. */
  readonly path: string;
  /** Connected system that supplies the file. */
  readonly system: string;
  /** Column order, verbatim from the demo. */
  readonly cols: readonly string[];
  /** Rows, fetched on demand. */
  load(): Promise<readonly Record<string, unknown>[]>;
}

/** Table keys `srcClick` can reach. A subset of the demo's seventeen. */
export type SourceTableKey =
  | 'wos'
  | 'health'
  | 'shift'
  | 'metric'
  | 'ledger_c'
  | 'ledger_mo'
  | 'cpm'
  | 'energy'
  | 'efac'
  | 'inv'
  | 'fleet';

/** Demo line 1875. The eleven `SRC` entries `srcClick` routes to. */
export const SOURCE_TABLES: Readonly<Record<SourceTableKey, SourceTableSpec>> = {
  wos: {
    title: 'Work orders',
    path: '02_sap_pm/work_orders.csv',
    system: 'SAP PM',
    cols: [
      'wo_id', 'mine', 'machine_id', 'wo_type', 'pm_code', 'status', 'created', 'component',
      'downtime_h', 'labour_h', 'crew', 'labour_cost_aud', 'parts_cost_aud', 'freight_cost_aud',
      'external_cost_aud', 'downtime_cost_aud', 'total_direct_cost_aud', 'root_cause',
    ],
    load: async () => unpackTable<WorkOrder>((await loadOemDomain('work-orders')).wos) as unknown as Record<string, unknown>[],
  },
  health: {
    title: 'Component condition register',
    path: '00_fleet/component_health.csv',
    system: 'Meridian Maximo · MERSVC',
    cols: ['machine_id', 'component', 'health_score', 'status', 'hours_since_change', 'note'],
    load: async () => HEALTH as unknown as Record<string, unknown>[],
  },
  shift: {
    title: 'Shift records (daily rollup)',
    path: '01_telematics/shift_end/shift_records_all_mines.csv',
    system: 'Pitram',
    cols: ['machine_id', 'date', 'hours', 'alarms', 'abuse', 'diesel_l', 'kwh', 'co2_kg', 'delays'],
    load: async () =>
      unpackTable<ShiftDailyRow>((await loadOemDomain('telemetry')).shift_daily) as unknown as Record<string, unknown>[],
  },
  metric: {
    title: 'Daily key metric',
    path: '01_telematics/daily_key_metric_all_machines.csv',
    system: 'Live telematics',
    cols: ['machine_id', 'date', 'metric', 'value', 'utilisation_pct'],
    load: async () =>
      unpackTable<DailyMetricRow>((await loadOemDomain('telemetry')).daily_metric) as unknown as Record<string, unknown>[],
  },
  ledger_c: {
    title: 'Cost ledger · by crew and category',
    path: '09_costs/cost_by_crew.csv',
    system: 'Cost ledger',
    cols: ['mine', 'crew', 'category', 'amount_aud'],
    load: async () => (await loadOemDomain('costs')).cost_by_crew_cat as unknown as Record<string, unknown>[],
  },
  ledger_mo: {
    title: 'Cost ledger · by month, mine, category',
    path: '09_costs/cost_ledger.csv (aggregated)',
    system: 'Cost ledger',
    cols: ['month', 'mine', 'category', 'amount_aud'],
    load: async () => (await loadOemDomain('costs')).cost_by_month_mine as unknown as Record<string, unknown>[],
  },
  cpm: {
    title: 'Cost per operating hour',
    path: '09_costs/cost_per_machine.csv',
    system: 'Cost ledger',
    cols: ['machine_id', 'amount_aud', 'op_hours_18m', 'cost_per_op_hour_aud'],
    load: async () => COST_PER_MACHINE as unknown as Record<string, unknown>[],
  },
  energy: {
    title: 'Energy and emissions · 12 days',
    path: '09_costs/energy_by_machine_12d.csv',
    system: 'Pitram · live telematics',
    cols: [
      'mine', 'machine_id', 'model', 'drive', 'hours', 'diesel_l', 'energy_kwh', 'co2_kg',
      'co2_kg_per_hour', 'energy_cost_aud',
    ],
    load: async () => (await loadOemDomain('telemetry')).energy as unknown as Record<string, unknown>[],
  },
  efac: {
    title: 'Energy factors',
    path: '09_costs/energy_factors.csv',
    system: 'Cost ledger',
    cols: [
      'mine', 'power_source', 'grid_kgco2_per_kwh', 'diesel_kgco2_per_l', 'diesel_aud_per_l',
      'power_aud_per_kwh',
    ],
    load: async () => ENERGY_FACTORS as unknown as Record<string, unknown>[],
  },
  inv: {
    title: 'Inventory snapshot',
    path: '03_parts_salesforce/inventory_snapshot.csv',
    system: 'Salesforce',
    cols: ['location', 'part_no', 'qty_on_hand', 'min_level'],
    load: async () => (await loadOemDomain('parts')).inventory as unknown as Record<string, unknown>[],
  },
  fleet: {
    title: 'Fleet register',
    path: '00_fleet/fleet_register.csv',
    system: 'Meridian Maximo · MERSVC',
    cols: [
      'machine_id', 'mine', 'model', 'division', 'subclass', 'drive', 'operating_mode', 'serial',
      'commissioned', 'engine_hours', 'list_price_aud',
    ],
    load: async () => FLEET as unknown as Record<string, unknown>[],
  },
};

/** Rows shown in the drawer before the slice is truncated. */
export const SOURCE_ROW_LIMIT = 40;

/**
 * Build the drawer payload for one table, optionally filtered to a machine.
 *
 * Demo line 1907 — `audit(srcKey, filter)`. The provenance line is this port's
 * addition: the demo showed the path and format in the drawer chrome; here it
 * is one string, `'<system> · <path> · <n> of <total> rows · extracted 09 Sep 2026'`.
 */
export async function sourceTablePayload(
  key: SourceTableKey,
  filter?: { machine_id?: string },
): Promise<SourceTablePayload> {
  const spec = SOURCE_TABLES[key];
  const all = await spec.load();
  const rows = filter?.machine_id
    ? all.filter((r) => r.machine_id === filter.machine_id)
    : all;
  const shown = rows.slice(0, SOURCE_ROW_LIMIT);
  return {
    kind: 'table',
    title: spec.title,
    columns: [...spec.cols],
    rows: shown.map((r) =>
      spec.cols.map((c) => {
        const v = r[c];
        if (v == null) return null;
        return typeof v === 'number' ? v : String(v);
      }),
    ),
    provenance: `${spec.system} · ${spec.path} · ${shown.length.toLocaleString('en-AU')} of ${all.length.toLocaleString('en-AU')} rows · extracted 09 Sep 2026`,
  };
}

// ---------------------------------------------------------------------------
// srcClick
// ---------------------------------------------------------------------------

/**
 * Demo line 4034. The label → `SRC` key map, deduplicated.
 *
 * `'__reports'` is the demo's sentinel for "go to the Reports page instead of
 * opening a drawer".
 */
export const SOURCE_MAP: readonly (readonly [string, SourceTableKey | '__reports'])[] = [
  ['generate_report', '__reports'],
  ['condition_register', 'health'],
  ['shift_records', 'shift'],
  ['inventory_snapshot', 'inv'],
  ['cost_ledger_by_month', 'ledger_mo'],
  ['cost_per_operating_hour', 'cpm'],
  ['energy_by_machine', 'energy'],
  ['rate_card', 'efac'],
  ['machine_history', 'wos'],
  ['work_orders', 'wos'],
  ['breakdown_trend', 'wos'],
  ['fault_trend', 'metric'],
  ['predict_risk', 'health'],
  ['cost_summary', 'ledger_c'],
  ['cost_ledger', 'ledger_c'],
  ['stock_check', 'inv'],
  ['energy_summary', 'energy'],
  ['energy_factors', 'efac'],
  ['component_health', 'health'],
  ['fleet_overview', 'fleet'],
  ['telemetry', 'metric'],
];

const MACHINE_RE = /\b([JLPTS]-\d\d)\b/;

/**
 * Demo line 4034. `srcClick(s)` — resolve one source-chip label.
 *
 * Async because a table slice has to fetch its domain first. The order of the
 * five tests is the demo's, and so is every regex.
 */
export async function resolveSource(label: string): Promise<SourceResolution> {
  const s = label.trim();
  const m = MACHINE_RE.exec(s);
  const lower = s.toLowerCase();

  // 1 · a controlled document, matched on the file's first underscore segment.
  const docs = await loadDocIndexSafely();
  const doc = docs.find((x) => {
    const stem = x.file.split('_')[0];
    return stem.length > 5 && s.includes(stem);
  });
  if (doc) return { kind: 'route', to: `/integration/data?doc=${encodeURIComponent(doc.file)}` };

  // 2 · a slice of a source system's table.
  for (const [key, target] of SOURCE_MAP) {
    if (lower.includes(key.replace(/_/g, ' ')) || lower.includes(key)) {
      if (target === '__reports') {
        return { kind: 'route', to: routeForView('reports') ?? '/reports' };
      }
      const payload = await sourceTablePayload(
        target,
        m && hasMachine(m[1]) ? { machine_id: m[1] } : undefined,
      );
      return { kind: 'drawer', payload };
    }
  }

  // 3 · the three literal labels the demo special-cased.
  if (/^alerts$/i.test(s)) return { kind: 'route', to: routeForView('alerts') ?? '/issues/alerts' };
  if (/^orders$/i.test(s)) {
    return { kind: 'drawer', payload: await sourceTablePayload('inv') };
  }
  if (/email|outlook/i.test(s)) {
    return { kind: 'route', to: routeForView('data') ?? '/integration/data' };
  }

  // 4 · a bare machine id.
  if (m && hasMachine(m[1])) {
    return { kind: 'route', to: routeForView('machine', m[1]) ?? `/machines/${m[1]}` };
  }

  // 5 · the demo's `toast(s)`.
  return { kind: 'none', label: s };
}

/** `doc-index` is 327 KB; a failed fetch must not break a citation click. */
async function loadDocIndexSafely(): Promise<readonly { file: string }[]> {
  try {
    return (await loadOemDomain('doc-index')).docs;
  } catch {
    return [];
  }
}
